import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";

const EASE = [0.22, 1, 0.36, 1];

// Real client work images from projeler
const GRID_ITEMS = [
  {
    // Tall left column: vibrant social media post  
    src: "/projeler/postunhazir_1776700579_3879568086261997846_40209662941.jpg",
    alt: "Sosyal Medya Kampanyası",
    rotate: "-3deg",
    delay: 0.3,
    label: "Kampanya",
    color: "bg-brand-pink",
    isCampaign: true,  // renders special campaign card
  },
  {
    src: "/projeler/postunhazir_1776700579_3879568086261997846_40209662941.jpg",
    alt: "Meta Ads Kreatif",
    rotate: "2.5deg",
    delay: 0.45,
    label: "Meta Ads",
    color: "bg-gold",
  },
  {
    src: "/projeler/postunhazir_1733155802_3514288033514426975_40209662941.jpg",
    alt: "Kreatif Üretim",
    rotate: "-2deg",
    delay: 0.6,
    label: "Kreatif",
    color: "bg-postun-blue",
  },
  {
    src: "/projeler/postunhazir_1709195261_3313292454739286208_40209662941.jpg",
    alt: "Sosyal Medya",
    rotate: "3deg",
    delay: 0.75,
    label: "Sosyal Medya",
    color: "bg-brand-magenta",
  },
  // 5th tile - empty gap filler: AI/Automation themed card (CSS gradient)
  {
    src: null, // CSS-based card
    alt: "AI & Otomasyon",
    rotate: "-2deg",
    delay: 0.88,
    label: "AI Tools",
    color: "bg-ink",
    isAI: true,
  },
  {
    src: "/projeler/postunhazir_1776700579_3879568086815659792_40209662941.jpg",
    alt: "SEO & Performans",
    rotate: "-1.5deg",
    delay: 1.0,
    label: "SEO",
    color: "bg-ink",
  },
];

// Floating badge component
function GridBadge({ label, color = "bg-brand-pink" }) {
  return (
    <span
      className={`absolute bottom-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-full ${color} text-white text-[0.6rem] font-bold uppercase tracking-[0.14em] px-2.5 py-1 backdrop-blur-sm`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-white opacity-80" />
      {label}
    </span>
  );
}

// AI/Automation themed card
function AICard({ rotate, delay, aiLabel, setupLabel }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, rotate: 0 }}
      animate={{ opacity: 1, y: 0, rotate: rotate }}
      transition={{ duration: 0.9, delay, ease: EASE }}
      className="relative overflow-hidden rounded-2xl shadow-[0_14px_40px_-10px_rgba(0,0,0,0.35)] border border-white/20 aspect-[4/3]"
      style={{
        transformOrigin: "center bottom",
        background: "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
      }}
    >
      {/* Animated grid lines */}
      <div className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "linear-gradient(rgba(238,79,122,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(238,79,122,0.4) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />
      {/* Glowing nodes */}
      <div className="absolute top-4 left-6 h-3 w-3 rounded-full bg-brand-pink shadow-[0_0_12px_4px_rgba(238,79,122,0.7)]" />
      <div className="absolute top-8 right-8 h-2 w-2 rounded-full bg-postun-cyan shadow-[0_0_10px_3px_rgba(124,237,217,0.7)]" />
      <div className="absolute bottom-10 left-10 h-2.5 w-2.5 rounded-full bg-gold shadow-[0_0_10px_3px_rgba(212,175,55,0.7)]" />
      <div className="absolute bottom-6 right-6 h-2 w-2 rounded-full bg-brand-pink shadow-[0_0_8px_3px_rgba(238,79,122,0.5)]" />
      {/* Connection lines SVG */}
      <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 200 150">
        <line x1="24" y1="16" x2="168" y2="32" stroke="#EE4F7A" strokeWidth="1" strokeDasharray="4,3" />
        <line x1="168" y1="32" x2="40" y2="110" stroke="#7CEDD9" strokeWidth="1" strokeDasharray="4,3" />
        <line x1="40" y1="110" x2="168" y2="120" stroke="#D4AF37" strokeWidth="1" strokeDasharray="4,3" />
        <circle cx="24" cy="16" r="3" fill="#EE4F7A" />
        <circle cx="168" cy="32" r="3" fill="#7CEDD9" />
        <circle cx="40" cy="110" r="3" fill="#D4AF37" />
        <circle cx="168" cy="120" r="3" fill="#EE4F7A" />
      </svg>
      {/* Center icon */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5">
        <div className="flex items-center gap-1.5">
          <svg viewBox="0 0 24 24" className="h-7 w-7 text-brand-pink" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z" />
            <circle cx="9" cy="13" r="1.5" fill="currentColor" />
            <circle cx="15" cy="13" r="1.5" fill="currentColor" />
          </svg>
          <span className="text-white font-bold text-xs tracking-wide">AI</span>
          <svg viewBox="0 0 24 24" className="h-5 w-5 text-postun-cyan" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <rect x="2" y="3" width="20" height="14" rx="2" />
            <path d="M8 21h8M12 17v4" />
            <path d="M7 7h.01M12 7h.01M17 7h.01" strokeWidth="2" strokeLinecap="round" />
            <path d="M7 11h10" strokeLinecap="round" />
          </svg>
        </div>
        <div className="text-center px-2">
          <div className="text-white text-[0.65rem] font-bold uppercase tracking-[0.15em] leading-tight">
            {aiLabel}
          </div>
          <div className="text-white/50 text-[0.55rem] mt-0.5">
            {setupLabel}
          </div>
        </div>
      </div>
      <GridBadge label="AI Tools" color="bg-brand-pink" />
      {/* Scan line animation */}
      <div
        className="absolute inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-brand-pink to-transparent opacity-60"
        style={{ animation: "scanline 3s linear infinite", top: "20%" }}
      />
      <style>{`
        @keyframes scanline {
          0% { top: 0%; opacity: 0.8; }
          100% { top: 100%; opacity: 0; }
        }
      `}</style>
    </motion.div>
  );
}

export default function HeroVideoGrid() {
  const { t } = useLanguage();
  
  // Translatable labels
  const labels = {
    campaign: t("hero.grid.campaign", "Kampanya"),
    metaAds: "Meta Ads",
    creative: t("hero.grid.creative", "Kreatif"),
    socialMedia: t("hero.grid.social_media", "Sosyal Medya"),
    aiTools: "AI Tools",
    seo: "SEO",
    avgReturn: t("hero.grid.avg_return", "Ort. Getiri"),
    aiAutomation: t("hero.grid.ai_automation", "AI Otomasyon"),
    setupIntegration: t("hero.grid.setup_integration", "Kurulum & Entegrasyon"),
    yearsExp: t("hero.grid.years_exp", "15+ Yıl"),
    experience: t("hero.grid.experience", "Deneyim"),
    projects: t("hero.grid.projects", "200+ Proje"),
    completed: t("hero.grid.completed", "Başarıyla Tamamlandı"),
  };
  return (
    <div
      className="relative w-full h-full flex items-center justify-end"
      aria-hidden="true"
    >
      {/* Soft glow backdrop */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div
          className="h-72 w-72 rounded-full opacity-30 blur-3xl"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, #EE4F7A 0%, #C026D3 50%, transparent 100%)",
          }}
        />
      </div>

      {/* Grid layout — 2 columns, 4 rows, staggered */}
      <div className="relative w-full max-w-[520px] grid grid-cols-2 gap-3">

        {/* Row 1-2: Left tall (col1, row-span-2) — Campaign Card */}
        <motion.div
          initial={{ opacity: 0, y: 30, rotate: 0 }}
          animate={{ opacity: 1, y: 0, rotate: GRID_ITEMS[0].rotate }}
          transition={{ duration: 0.9, delay: GRID_ITEMS[0].delay, ease: EASE }}
          className="relative col-span-1 row-span-2 overflow-hidden rounded-2xl shadow-[0_20px_60px_-15px_rgba(238,79,122,0.5)] border border-white/20"
          style={{ transformOrigin: "center bottom", minHeight: 280 }}
        >
          {/* Creative campaign card design */}
          <div
            className="h-full w-full flex flex-col"
            style={{
              background: "linear-gradient(160deg, #1a1a2e 0%, #16213e 40%, #0f3460 70%, #533483 100%)",
              minHeight: 280,
            }}
          >
            {/* Stars/grid overlay */}
            <div className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: "radial-gradient(circle, rgba(238,79,122,0.5) 1px, transparent 1px)",
                backgroundSize: "18px 18px",
              }}
            />
            {/* Glowing orbs */}
            <div className="absolute top-4 right-4 h-20 w-20 rounded-full opacity-40 blur-xl"
              style={{ background: "radial-gradient(circle, #EE4F7A 0%, transparent 70%)" }} />
            <div className="absolute bottom-16 left-3 h-16 w-16 rounded-full opacity-30 blur-xl"
              style={{ background: "radial-gradient(circle, #7CEDD9 0%, transparent 70%)" }} />

            {/* Content */}
            <div className="relative z-10 flex flex-col h-full p-4 pt-5">
              {/* Top metric badge */}
              <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur rounded-full px-2.5 py-1 w-fit mb-4">
                <div className="h-1.5 w-1.5 rounded-full bg-brand-pink" />
                <span className="text-white text-[0.55rem] font-bold uppercase tracking-widest">ROAS</span>
              </div>

              {/* Big number */}
              <div className="text-white font-display font-black leading-none" style={{ fontSize: "clamp(2.8rem, 5vw, 3.5rem)" }}>
                3.2<span className="text-brand-pink">x</span>
              </div>
              <div className="text-white/60 text-[0.65rem] uppercase tracking-widest mt-1">{labels.avgReturn}</div>

              {/* Bar chart visual */}
              <div className="mt-auto mb-3 flex items-end gap-1.5 h-16">
                {[40, 60, 45, 80, 55, 100, 75].map((h, i) => (
                  <div key={i} className="flex-1 rounded-t-sm" style={{
                    height: `${h}%`,
                    background: i === 5
                      ? "linear-gradient(to top, #EE4F7A, #C026D3)"
                      : `rgba(255,255,255,${0.1 + i * 0.05})`,
                  }} />
                ))}
              </div>

              {/* Campaign label */}
              <div className="flex items-center justify-between">
                <span className="text-white/80 text-[0.6rem] font-semibold uppercase tracking-wider">Meta Ads</span>
                <div className="flex items-center gap-1">
                  <svg viewBox="0 0 12 12" className="h-3 w-3 text-green-400" fill="currentColor">
                    <path d="M2 8l3-5 2.5 3.5L9 4l1 4H2z" />
                  </svg>
                  <span className="text-green-400 text-[0.6rem] font-bold">+127%</span>
                </div>
              </div>
            </div>

            {/* Badge */}
            <GridBadge label={labels.campaign} color="bg-brand-pink" />
          </div>
        </motion.div>

        {/* Right col top (col2, row1) */}
        <motion.div
          initial={{ opacity: 0, y: 30, rotate: 0 }}
          animate={{ opacity: 1, y: 0, rotate: GRID_ITEMS[1].rotate }}
          transition={{ duration: 0.9, delay: GRID_ITEMS[1].delay, ease: EASE }}
          className="relative overflow-hidden rounded-2xl shadow-[0_14px_40px_-10px_rgba(0,0,0,0.25)] border border-white/20"
          style={{ transformOrigin: "center bottom" }}
        >
          <img
            src={GRID_ITEMS[1].src}
            alt={GRID_ITEMS[1].alt}
            className="w-full aspect-[4/3] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          <GridBadge label="Meta Ads" color={GRID_ITEMS[1].color} />
        </motion.div>

        {/* Right col middle (col2, row2) */}
        <motion.div
          initial={{ opacity: 0, y: 30, rotate: 0 }}
          animate={{ opacity: 1, y: 0, rotate: GRID_ITEMS[2].rotate }}
          transition={{ duration: 0.9, delay: GRID_ITEMS[2].delay, ease: EASE }}
          className="relative overflow-hidden rounded-2xl shadow-[0_14px_40px_-10px_rgba(0,0,0,0.25)] border border-white/20"
          style={{ transformOrigin: "center bottom" }}
        >
          <img
            src={GRID_ITEMS[2].src}
            alt={GRID_ITEMS[2].alt}
            className="w-full aspect-[4/3] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          <GridBadge label={labels.creative} color={GRID_ITEMS[2].color} />
        </motion.div>

        {/* Row 3 left (col1, row3) */}
        <motion.div
          initial={{ opacity: 0, y: 30, rotate: 0 }}
          animate={{ opacity: 1, y: 0, rotate: GRID_ITEMS[3].rotate }}
          transition={{ duration: 0.9, delay: GRID_ITEMS[3].delay, ease: EASE }}
          className="relative overflow-hidden rounded-2xl shadow-[0_14px_40px_-10px_rgba(0,0,0,0.25)] border border-white/20"
          style={{ transformOrigin: "center top" }}
        >
          <img
            src={GRID_ITEMS[3].src}
            alt={GRID_ITEMS[3].alt}
            className="w-full aspect-[4/3] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          <GridBadge label={labels.socialMedia} color={GRID_ITEMS[3].color} />
        </motion.div>

        {/* Row 3 right (col2, row3) — AI Automation card fills the gap! */}
        <AICard rotate={GRID_ITEMS[4].rotate} delay={GRID_ITEMS[4].delay} aiLabel={labels.aiAutomation} setupLabel={labels.setupIntegration} />

        {/* Row 4: Full width (col-span-2, row4) */}
        <motion.div
          initial={{ opacity: 0, y: 30, rotate: 0 }}
          animate={{ opacity: 1, y: 0, rotate: GRID_ITEMS[5].rotate }}
          transition={{ duration: 0.9, delay: GRID_ITEMS[5].delay, ease: EASE }}
          className="relative col-span-2 overflow-hidden rounded-2xl shadow-[0_14px_40px_-10px_rgba(0,0,0,0.25)] border border-white/20"
          style={{ transformOrigin: "center top" }}
        >
          <img
            src={GRID_ITEMS[5].src}
            alt={GRID_ITEMS[5].alt}
            className="w-full aspect-[21/7] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <GridBadge label="SEO" color={GRID_ITEMS[5].color} />

          {/* Floating stat badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 1.3, ease: EASE }}
            className="absolute top-3 right-3 rounded-xl bg-white/95 backdrop-blur px-3 py-2 text-ink shadow-lg"
          >
            <div className="text-xs font-bold text-brand-pink">{labels.yearsExp}</div>
            <div className="text-[0.6rem] text-ink/60 leading-none mt-0.5">{labels.experience}</div>
          </motion.div>
        </motion.div>
      </div>

      {/* Floating metrics ribbon */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 1.4, ease: EASE }}
        className="absolute -bottom-4 -left-4 flex items-center gap-3 rounded-2xl bg-white/90 backdrop-blur-md shadow-xl border border-mist px-4 py-3"
        style={{ zIndex: 20 }}
      >
        <div className="flex items-center justify-center h-9 w-9 rounded-full bg-brand-pink/10">
          <svg viewBox="0 0 24 24" className="h-5 w-5 text-brand-pink" fill="none" stroke="currentColor" strokeWidth={2}>
            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
            <polyline points="16 7 22 7 22 13" />
          </svg>
        </div>
        <div>
          <div className="text-sm font-bold text-ink leading-none">{labels.projects}</div>
          <div className="text-xs text-ink/50 mt-0.5">{labels.completed}</div>
        </div>
      </motion.div>
    </div>
  );
}
